import React from 'react';
import '../styles/RulesScreen.css';

const RulesScreen = ({ onContinue, onBack, hasActiveGame, onBackToGame, currentVariant, isAuthenticated }) => {
  
  // Variant display names
  const variantNames = {
    'standard': 'Standard Chess',
    'kings_court_2dukes': 'Chess Kingdom (Two Dukes)',
    'players_choice': "Player's Choice",
    'instant_king': 'Instant King',
    'fight_to_be_king': 'Fight to be King',
    'pawns_wild': "King's Court: Pawn's Wild",
    'handicap_mode': 'Standard Chess vs Royal Chess Kingdom'
  };
  
  // If currentVariant is provided (in-game), show only that variant's rules
  const showOnlyCurrentVariant = !!currentVariant;
  
  // Helper functions to determine what sections to show based on current variant
  const showSection = (sectionVariants) => {
    if (!showOnlyCurrentVariant) return true; // Show all when not in-game
    if (sectionVariants === 'all') return true; // Core rules shown to all
    if (sectionVariants === 'kingdom') return currentVariant !== 'standard'; // Kingdom-specific rules
    return sectionVariants.includes(currentVariant); // Specific variant rules
  };
  
  return (
    <div className="rules-screen">
      <div className="rules-container">
        {showOnlyCurrentVariant ? (
          <h1>{variantNames[currentVariant] || 'Chess Kingdom'} Rules</h1>
        ) : (
          <h1>Chess Kingdom Rules</h1>
        )}
        
        <div className="rules-content">
          {/* Game Setup - Show for all Kingdom variants, hide for Standard Chess */}
          {showSection('kingdom') && (
          <section className="rule-section">
            <h2>🎮 Game Setup</h2>
            <p>Chess Kingdom is played on a standard 8×8 board with a special variant piece: the <strong>Duke</strong>.</p>
            <p><strong>Board Layout:</strong> R N Du Q K B N R</p>
            <ul>
              <li>Rook, Knight, <strong>Duke</strong>, Queen, King, Bishop, Knight, Rook</li>
              <li>Duke replaces one Bishop on the c-file (c1 for White, c8 for Black)</li>
            </ul>
          </section>
          )}

          {/* Duke Rules - Show for all Kingdom variants */}
          {showSection('kingdom') && (
          <section className="rule-section highlight">
            <h2>👑 The Duke - Special Variant Piece</h2>
            <p className="intro-text">The Duke is a powerful piece that combines limited movement with unique capturing abilities:</p>
            
            <div className="duke-rules">
              <div className="duke-rule-item">
                <h3>📐 Non-Capturing Moves:</h3>
                <p>The Duke moves <strong>exactly 1 square diagonally</strong></p>
                <p className="direction-arrows">↗ ↖ ↙ ↘</p>
                <ul>
                  <li>Only 4 diagonal directions</li>
                  <li>Cannot move orthogonally (up/down/left/right) unless capturing</li>
                  <li>Similar to a one-square Bishop move</li>
                </ul>
              </div>
              
              <div className="duke-rule-item">
                <h3>⚔️ Capturing Moves:</h3>
                <p>The Duke can capture on <strong>any adjacent square</strong></p>
                <p className="direction-arrows">↑ ↓ ← → ↗ ↖ ↙ ↘</p>
                <ul>
                  <li>All 8 surrounding squares</li>
                  <li>Can capture orthogonally (up/down/left/right)</li>
                  <li>Can capture diagonally as well</li>
                  <li>Must land on the captured piece's square</li>
                </ul>
              </div>
            </div>
            
            <div className="important-note">
              <strong>⚡ Key Rule:</strong> The Duke can move orthogonally (↑ ↓ ← →) ONLY when capturing an enemy piece. For non-capturing moves, it's limited to diagonal movement only.
            </div>
          </section>
          )}

          {/* King Capture Rules - Show for all Kingdom variants */}
          {showSection('kingdom') && (
          <section className="rule-section">
            <h2>⚔️ King Capture & Duke Transformation</h2>
            <div className="transformation-info">
              <p><strong>Unlike traditional chess, Kings CAN be captured in Chess Kingdom!</strong></p>
              
              <h3>What Happens When a King is Captured:</h3>
              <ol>
                <li>The captured King is removed from the board</li>
                <li>The Duke of that side <strong>immediately transforms into a King</strong></li>
                <li>The new King (transformed Duke) gains full King movement</li>
                <li>The game continues with the new King</li>
                <li>The royal indicator ring moves to the new King's position</li>
              </ol>

              <h3>Transformed Duke Abilities:</h3>
              <ul>
                <li>✓ Moves like a King (8 directions, 1 square each)</li>
                <li>✓ Can castle (if conditions are met)</li>
                <li>✓ Must be protected from check</li>
                <li>✓ Can be put in checkmate (ends the game)</li>
              </ul>
            </div>
          </section>
          )}

          {/* Embodiment Rules - Show only for variants with embodiment */}
          {showSection(['instant_king', 'fight_to_be_king', 'players_choice', 'kings_court_2dukes']) && (
          <section className="rule-section embodiment-section">
            <h2>🛡️ Embodiment Modes - Choose Your Strategy</h2>
            <p className="intro-text">At the start of the game, each player selects an embodiment mode that determines the visual appearance of their Duke and the royal indicator style:</p>
            
            <div className="embodiment-modes">
              <div className="mode-item instant-king">
                <div className="mode-header">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_royal-chess-duke/artifacts/86uzfhb1_the%20Instant%20K.png" 
                    alt="Instant King" 
                    className="mode-icon"
                  />
                  <h3>👑 Instant King</h3>
                </div>
                <div className="mode-description">
                  <h4>Duke Appearance:</h4>
                  <p>Crown shield - symbolizing immediate royal succession</p>
                  
                  <h4>Transformation:</h4>
                  <p>When your King is captured, your Duke <strong>instantly becomes the new King</strong></p>
                  
                  <h4>Royal Indicator:</h4>
                  <ul>
                    <li>Golden ring for White King</li>
                    <li>Cyan ring for Black King</li>
                    <li>Ring follows current King position</li>
                    <li>Turns red and pulses when in check</li>
                  </ul>
                  
                  <h4>Strategy:</h4>
                  <p>Standard succession - focus on piece development and King safety</p>
                </div>
              </div>

              <div className="mode-item fight-to-be-king">
                <div className="mode-header">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_royal-chess-duke/artifacts/ttvvcz0k_fight%20to%20be%20king.png" 
                    alt="Fight to be King" 
                    className="mode-icon"
                  />
                  <h3>⚔️ Fight to be King</h3>
                </div>
                <div className="mode-description">
                  <h4>Duke Appearance:</h4>
                  <p>Helmet shield - symbolizing the warrior's path to kingship</p>
                  
                  <h4>Transformation (DIFFERENT RULES!):</h4>
                  <div className="special-rule-box">
                    <p><strong>When your King is captured:</strong></p>
                    <ol>
                      <li>Duke does NOT immediately transform</li>
                      <li>Duke must <strong>reach the baseline</strong> (back rank)</li>
                      <li>White Duke: Must reach rank 1 (a1-h1)</li>
                      <li>Black Duke: Must reach rank 8 (a8-h8)</li>
                      <li>Once Duke reaches baseline → Transforms to King</li>
                    </ol>
                    <p className="warning">⚠️ Until Duke reaches baseline, you have NO King!</p>
                    <p className="warning">⚠️ Duke must be protected until transformation</p>
                  </div>
                  
                  <h4>Royal Indicator:</h4>
                  <ul>
                    <li>Before transformation: No indicator (Duke is not yet royal)</li>
                    <li>After reaching baseline: Ring appears on new King</li>
                    <li><strong>Special notch marker at 12 o'clock position</strong></li>
                    <li>Notch indicates "earned kingship" status</li>
                    <li>Turns red and pulses when in check</li>
                  </ul>
                  
                  <h4>Strategy:</h4>
                  <p><strong>High Risk, High Reward!</strong></p>
                  <ul>
                    <li>Must maneuver Duke back to baseline</li>
                    <li>Duke is vulnerable during journey</li>
                    <li>Requires tactical planning after King capture</li>
                    <li>Opponent can target Duke before transformation</li>
                  </ul>

                  <h4>👑 Professional Mode (Optional):</h4>
                  <div className="special-rule-box">
                    <p><strong>When enabled, adds a stricter restriction after your King is captured:</strong></p>
                    <ul>
                      <li>The restricted Duke <strong>loses all orthogonal (up/down/left/right) captures</strong> — only diagonal captures remain</li>
                      <li>A pulsing <span style={{color: '#FF8A1E', fontWeight: 'bold'}}>orange ring</span> appears on the restricted Duke</li>
                      <li>The orange ring shows alongside the red check ring when the Duke is in check</li>
                      <li>The restricted Duke <em>cannot</em> threaten squares orthogonally — opposing Kings may walk next to it safely</li>
                    </ul>
                    <p><strong>Pro 1 — Any move</strong>: any move (capture or not) to the baseline reclaims the throne.</p>
                    <p><strong>Pro 2 — Non-capture only</strong>: only a non-capturing move to the baseline reclaims the throne.
                      <br/><strong>⚠️ Additional restriction:</strong> the restricted Duke in Pro 2 <em>cannot</em> move onto the opponent's baseline rank while it remains a Duke — you must reclaim your throne on your own baseline only.
                    </p>
                    <p><strong>Per-side (Asymmetric)</strong>: in solo and multiplayer, you can assign White and Black different Pro modes (e.g., White plays Classic while Black plays Pro 2) for deeply asymmetric duels.</p>
                    <p><strong>🏆 Ranked:</strong> Pro 1 and Pro 2 each have their own dedicated ranked ELO ladder, separate from Classic.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="embodiment-notes">
              <h4>📝 Important Notes:</h4>
              <ul>
                <li>Both modes have identical transformation mechanics</li>
                <li>The difference is purely visual and thematic</li>
                <li>Each side can choose independently (White can be Instant King, Black can be Fight to be King, or any combination)</li>
                <li>The notch indicator only appears in Fight to be King mode</li>
                <li>Duke shields change based on your selected embodiment</li>
              </ul>
            </div>
          </section>
          )}

          {/* Two Dukes Variant - Show only for Two Dukes variant */}
          {showSection(['kings_court_2dukes']) && (
          <section className="rule-section two-dukes-section">
            <h2>👥 Chess Kingdom (King's Court) - Two Dukes Variant</h2>
            <p className="intro-text">Each player commands TWO Dukes with pre-assigned embodiment modes based on position!</p>
            
            <div className="two-dukes-setup">
              <h3>🎯 Board Setup:</h3>
              <p><strong>Formation:</strong> R-N-Du-Q-K-Du-N-R (NO Bishops!)</p>
              <ul>
                <li><strong>c1/c8 Duke</strong> (beside Queen) = <strong>⚡ Instant King Duke</strong></li>
                <li><strong>f1/f8 Duke</strong> (beside King & Knight) = <strong>⚔️ Fight to be King Duke</strong></li>
                <li>Embodiment modes are <strong>pre-assigned by position</strong></li>
                <li>Each player has one of each type</li>
              </ul>
            </div>

            <div className="two-dukes-rules">
              <h3>🔄 How It Works:</h3>
              <div className="special-rule-box">
                <p><strong>When Your King is Captured:</strong></p>
                <ol>
                  <li><strong>Choose Which Duke to Ascend:</strong>
                    <ul>
                      <li>Modal appears showing both Duke options</li>
                      <li>Choose <strong>⚡ Instant King Duke (c1/c8)</strong> = Transforms immediately</li>
                      <li>Choose <strong>⚔️ Fight to be King Duke (f1/f8)</strong> = Must reach baseline first</li>
                      <li>Decision is strategic based on board position!</li>
                    </ul>
                  </li>
                  <li><strong>If One Duke Already Captured:</strong>
                    <ul>
                      <li>⚡ Instant Duke captured → ⚔️ Fight Duke auto-selected (must reach baseline)</li>
                      <li>⚔️ Fight Duke captured → ⚡ Instant Duke auto-selected (transforms immediately)</li>
                      <li>No choice needed - only one Duke remaining</li>
                    </ul>
                  </li>
                  <li><strong>If Both Dukes Captured:</strong>
                    <ul>
                      <li>Game Over - You lose!</li>
                    </ul>
                  </li>
                </ol>
              </div>

              <h3>⚡ Instant King Duke (c1/c8 - beside Queen):</h3>
              <ul>
                <li>Transforms <strong>immediately</strong> when chosen after King capture</li>
                <li>Becomes a full King right away</li>
                <li>Can move, castle, and play normally</li>
                <li>Royal indicator appears immediately</li>
              </ul>

              <h3>⚔️ Fight to be King Duke (f1/f8 - beside King):</h3>
              <ul>
                <li>Does NOT transform immediately when chosen</li>
                <li>Must <strong>return to baseline</strong> to become King</li>
                <li>White: Must reach rank 1 (any square a1-h1)</li>
                <li>Black: Must reach rank 8 (any square a8-h8)</li>
                <li>If already at baseline when chosen → transforms immediately</li>
                <li>If not at baseline → must move there first</li>
              </ul>

              <h3>🎲 Strategic Decision:</h3>
              <div className="strategy-tips">
                <h4>Choose ⚡ Instant King Duke When:</h4>
                <ul>
                  <li>✓ Duke is in a <strong>safer, well-defended</strong> position</li>
                  <li>✓ You need a King <strong>immediately</strong> for defense</li>
                  <li>✓ Duke can control center or key squares right away</li>
                  <li>✓ ⚔️ Fight Duke is in a bad position or under attack</li>
                </ul>

                <h4>Choose ⚔️ Fight to be King Duke When:</h4>
                <ul>
                  <li>✓ Duke is <strong>already at baseline</strong> (instant transformation!)</li>
                  <li>✓ Duke has a <strong>clear, safe path</strong> to baseline</li>
                  <li>✓ ⚡ Instant Duke is in a poor position or under attack</li>
                  <li>✓ You want to use Instant Duke for other tactical purposes</li>
                </ul>
              </div>

              <h3>🎯 Key Advantages:</h3>
              <ul>
                <li><strong>Flexibility:</strong> Choose embodiment based on current board state</li>
                <li><strong>Two Chances:</strong> Losing one Duke doesn't end the game</li>
                <li><strong>Strategic Depth:</strong> Duke positioning matters throughout game</li>
                <li><strong>Tactical Options:</strong> Two Dukes provide extra attacking power</li>
              </ul>
            </div>

            <div className="important-note">
              <strong>💡 Remember:</strong> Embodiment is pre-assigned by position! c1/c8 = Instant, f1/f8 = Fight. You choose WHICH Duke becomes King, not which mode they have!
            </div>
          </section>
          )}

          {/* Game Ending - Show for all */}
          <section className="rule-section">
            <h2>🏁 Game Ending</h2>
            <div className="ending-rules">
              <div className="ending-item">
                <h3>✅ Checkmate:</h3>
                <p>The <strong>last remaining King</strong> is in check with no legal moves</p>
                <ul>
                  <li>King is under attack</li>
                  <li>Cannot move to safety</li>
                  <li>Cannot block the attack</li>
                  <li>Cannot capture the attacking piece</li>
                </ul>
                <p><strong>Winner:</strong> Opponent who delivered checkmate</p>
              </div>
              
              <div className="ending-item">
                <h3>🤝 Stalemate:</h3>
                <p>The <strong>last remaining King</strong> has no legal moves but is NOT in check</p>
                <ul>
                  <li>King is not under attack</li>
                  <li>Player has no legal moves available</li>
                  <li>Moving any piece would put King in check</li>
                </ul>
                <p><strong>Result:</strong> Draw (no winner)</p>
              </div>
            </div>
          </section>

          <section className="rule-section">
            <h2>♟️ Traditional Pieces</h2>
            <p>All other pieces follow <strong>standard chess rules</strong>:</p>
            <ul>
              <li><strong>Pawn:</strong> Forward 1-2 (from start), diagonal captures, en passant, promotion</li>
              <li><strong>Knight:</strong> L-shaped jumps (2+1), can jump over pieces</li>
              <li><strong>Bishop:</strong> Diagonal any distance, cannot jump over pieces</li>
              <li><strong>Rook:</strong> Horizontal/vertical any distance, participates in castling</li>
              <li><strong>Queen:</strong> Combines Rook + Bishop movement (8 directions, any distance)</li>
              <li><strong>King:</strong> One square any direction, castling ability</li>
            </ul>
          </section>

          {/* Standard Chess Specific Rules - Show only when playing Standard Chess */}
          {showSection(['standard']) && (
          <section className="rule-section highlight">
            <h2>♟️ Standard Chess Rules</h2>
            <p>You are playing traditional chess with standard rules:</p>
            <ul>
              <li><strong>No Dukes:</strong> Both Bishops remain on the board (c1/c8 positions)</li>
              <li><strong>Standard Checkmate:</strong> Game ends when King is checkmated</li>
              <li><strong>No King Capture:</strong> Kings cannot be captured, only checkmated</li>
              <li><strong>Standard Promotion:</strong> Pawns promote to Queen, Rook, Bishop, or Knight only</li>
              <li><strong>All Traditional Rules Apply:</strong> Castling, en passant, stalemate, etc.</li>
            </ul>
            <div className="important-note">
              <strong>📖 Note:</strong> This is classic chess without any Chess Kingdom modifications.
            </div>
          </section>
          )}

          {/* Key Differences - Show only for Kingdom variants */}
          {showSection('kingdom') && (
          <section className="rule-section highlight">
            <h2>⚡ Key Differences from Standard Chess</h2>
            <ol>
              <li><strong>Duke Piece:</strong> Replaces one Bishop (or two in Two Dukes variant) with unique movement rules</li>
              <li><strong>Kings Can Be Captured:</strong> Triggers Duke transformation instead of ending the game</li>
              <li><strong>Duke Transforms to King:</strong> Game continues with new King that has full King powers</li>
              <li><strong>Game Ends on Last King:</strong> Checkmate or stalemate of the transformed King ends the game</li>
              <li><strong>Embodiment Modes:</strong> Visual customization with thematic differences (pre-selected in standard variants)</li>
              <li><strong>Two Dukes Variant:</strong> Dynamic embodiment choice during gameplay when King is captured</li>
              <li><strong>Royal Indicators:</strong> Visible rings show which piece is currently the King</li>
            </ol>
          </section>
          )}

          {/* Game Variants - Show only when not in-game */}
          {!showOnlyCurrentVariant && (
          <section className="rule-section variant-selector">
            <h2>🎮 Game Variants Available</h2>
            <div className="variants-list">
              <div className="variant-item">
                <h3>♟️ Standard Chess</h3>
                <p>Traditional chess rules - no Dukes, no special mechanics</p>
              </div>
              
              <div className="variant-item">
                <h3>👑 Instant King Duke</h3>
                <p>One Duke per player - Instant transformation when King captured</p>
              </div>
              
              <div className="variant-item">
                <h3>⚔️ Fight to be King</h3>
                <p>One Duke per player - Must reach baseline to transform after King capture</p>
              </div>
              
              <div className="variant-item">
                <h3>🎯 Player's Choice</h3>
                <p>One Duke per player - Each player independently chooses their Duke's embodiment before game starts</p>
              </div>
              
              <div className="variant-item highlight-variant">
                <h3>👥 Chess Kingdom (King's Court) - Two Dukes</h3>
                <p>Two Dukes per player - Dynamic embodiment choice during gameplay</p>
                <p className="variant-note"><strong>Special!</strong> Choose which Duke ascends when your King is captured!</p>
              </div>
              
              <div className="variant-item highlight-variant">
                <h3>🛡️ King's Court: Pawn's Wild</h3>
                <p>Standard chess board - Pawns can promote to Dukes!</p>
                <p className="variant-note"><strong>New!</strong> When pawn reaches promotion, choose: Queen, Rook, Bishop, Knight, or Duke!</p>
              </div>
              
              <div className="variant-item highlight-variant">
                <h3>⚖️ Standard Chess vs Royal Chess Kingdom</h3>
                <p>Handicap Mode - One player with Duke, one with Bishop!</p>
                <p className="variant-note"><strong>Asymmetric!</strong> Perfect for different skill levels or learning Duke mechanics!</p>
              </div>
            </div>
          </section>
          )}

          {/* Pawn's Wild Variant - Show only for Pawn's Wild */}
          {showSection(['pawns_wild']) && (
          <section className="rule-section pawns-wild-section">
            <h2>🛡️ King's Court: Pawn's Wild - Pawn to Duke Promotion</h2>
            <p className="intro-text">A unique variant where pawns unlock the power to become Dukes!</p>
            
            <div className="pawns-wild-setup">
              <h3>🎯 Board Setup:</h3>
              <p><strong>Formation:</strong> R-N-B-Q-K-B-N-R (Standard Chess)</p>
              <ul>
                <li>Game starts with traditional chess pieces</li>
                <li>NO Dukes on the board at game start</li>
                <li>Bishops remain in their standard positions</li>
                <li>All pieces follow standard chess rules</li>
              </ul>
            </div>

            <div className="pawns-wild-rules">
              <h3>♟️ Pawn Promotion Rules:</h3>
              <div className="special-rule-box">
                <p><strong>When a pawn reaches the opposite end (rank 8 for White, rank 1 for Black):</strong></p>
                <p className="promotion-options">You can promote to:</p>
                <ul>
                  <li>👑 <strong>Queen</strong> - Most powerful piece</li>
                  <li>🏰 <strong>Rook</strong> - Controls ranks and files</li>
                  <li>⛪ <strong>Bishop</strong> - Controls diagonals</li>
                  <li>🐴 <strong>Knight</strong> - L-shaped jumps</li>
                  <li>🛡️ <strong>Duke</strong> - Special variant piece! (UNIQUE TO THIS VARIANT)</li>
                </ul>
              </div>

              <h3>🛡️ Promoting to Duke:</h3>
              <ul>
                <li><strong>Movement:</strong> Duke gets its special movement pattern:
                  <ul>
                    <li>Non-capturing: 1 square diagonally (↗ ↖ ↙ ↘)</li>
                    <li>Capturing: Any adjacent square (8 directions)</li>
                  </ul>
                </li>
                <li><strong>Embodiment:</strong> Promoted Dukes use <strong>Instant King</strong> embodiment by default</li>
                <li><strong>Transformation:</strong> If your King is captured, any Duke (including promoted ones) can transform into a King</li>
                <li><strong>Multiple Dukes:</strong> You can have multiple Dukes if you promote multiple pawns!</li>
              </ul>

              <h3>🎯 Strategic Considerations:</h3>
              <div className="strategy-tips">
                <h4>Why Promote to Duke?</h4>
                <ul>
                  <li>✓ <strong>King Insurance:</strong> Duke can become a King if your King is captured</li>
                  <li>✓ <strong>Unique Movement:</strong> Different tactical options than Queen/Rook/Bishop</li>
                  <li>✓ <strong>Flexible Capturing:</strong> Can capture in all 8 directions</li>
                  <li>✓ <strong>Endgame Power:</strong> Strong in close-quarters endgames</li>
                </ul>

                <h4>When to Choose Other Pieces?</h4>
                <ul>
                  <li>👑 <strong>Queen:</strong> Maximum immediate attacking power</li>
                  <li>🏰 <strong>Rook:</strong> Control open files and ranks</li>
                  <li>⛪ <strong>Bishop:</strong> Long-range diagonal control</li>
                  <li>🐴 <strong>Knight:</strong> Tactical flexibility, can jump pieces</li>
                </ul>
              </div>

              <h3>🔄 Duke Transformation (If King Captured):</h3>
              <ul>
                <li>When your King is captured, any of your Dukes can transform</li>
                <li>If you have multiple Dukes (from pawn promotions), choose which one transforms</li>
                <li>Promoted Dukes follow <strong>Instant King</strong> rules (immediate transformation)</li>
                <li>Game continues with your new King</li>
              </ul>
            </div>

            <div className="important-note">
              <strong>💡 Remember:</strong> Duke promotion is ONLY available in "King's Court: Pawn's Wild" variant! Other variants use standard promotion rules (Queen, Rook, Bishop, Knight only).
            </div>
          </section>
          )}

          {/* Handicap Mode Variant - Show only for Handicap Mode */}
          {showSection(['handicap_mode']) && (
          <section className="rule-section handicap-section">
            <h2>⚖️ Standard Chess vs Royal Chess Kingdom - Handicap Mode</h2>
            <p className="intro-text">An asymmetric variant where one player uses standard chess while the other wields the power of the Duke!</p>
            
            <div className="handicap-setup">
              <h3>🎯 Board Setup:</h3>
              <p><strong>Asymmetric Configuration - Choose Your Side!</strong></p>
              <ul>
                <li><strong>Royal Chess Player:</strong> R-N-D-Q-K-B-N-R (Duke on c1/c8)</li>
                <li><strong>Standard Chess Player:</strong> R-N-B-Q-K-B-N-R (Bishop on c1/c8)</li>
                <li>Choose which player (White or Black) gets the Duke</li>
                <li>Choose Duke embodiment (Instant King or Fight to be King)</li>
              </ul>
            </div>

            <div className="handicap-rules">
              <h3>⚖️ How Handicap Mode Works:</h3>
              <div className="special-rule-box">
                <p><strong>Before Game Starts:</strong></p>
                <ol>
                  <li><strong>Choose Duke Side:</strong> Select which player (White or Black) gets the Duke
                    <ul>
                      <li>Royal Chess Player: Has 1 Duke, can transform when King captured</li>
                      <li>Standard Chess Player: Has standard Bishops, NO Duke transformation</li>
                    </ul>
                  </li>
                  <li><strong>Choose Duke Embodiment:</strong> Select embodiment for Royal Chess player
                    <ul>
                      <li>⚡ Instant King: Duke transforms immediately when King captured</li>
                      <li>⚔️ Fight to be King: Duke must reach baseline to transform</li>
                    </ul>
                  </li>
                </ol>
              </div>

              <h3>👑 Royal Chess Player (Has Duke):</h3>
              <ul>
                <li>Board: R-N-<strong>Duke</strong>-Q-K-B-N-R</li>
                <li>Duke replaces one Bishop on c-file (c1 for White, c8 for Black)</li>
                <li>Duke has special movement (diagonal non-capturing, 8-direction capturing)</li>
                <li><strong>King Insurance:</strong> If King is captured, Duke can transform into new King</li>
                <li>Transformation follows chosen embodiment (Instant or Fight to be King)</li>
                <li><strong>Advantage:</strong> Extra life through Duke transformation</li>
              </ul>

              <h3>⛪ Standard Chess Player (Has Bishop):</h3>
              <ul>
                <li>Board: R-N-<strong>Bishop</strong>-Q-K-B-N-R</li>
                <li>Two Bishops (standard chess configuration)</li>
                <li>Follows traditional chess rules completely</li>
                <li><strong>NO Duke transformation ability</strong></li>
                <li>King capture = Immediate game loss</li>
                <li><strong>Advantage:</strong> Long-range Bishop power, familiar gameplay</li>
              </ul>

              <h3>🎯 Strategic Considerations:</h3>
              <div className="strategy-tips">
                <h4>Playing as Royal Chess (with Duke):</h4>
                <ul>
                  <li>✓ You have an extra chance if your King is captured</li>
                  <li>✓ Duke provides unique tactical opportunities</li>
                  <li>✓ Can play more aggressively knowing Duke is backup</li>
                  <li>✓ Must protect Duke after King capture</li>
                  <li>⚠️ Duke movement is less flexible than Bishop for long-range attacks</li>
                </ul>

                <h4>Playing as Standard Chess (with Bishop):</h4>
                <ul>
                  <li>✓ Bishops provide long-range diagonal control</li>
                  <li>✓ Familiar piece movement and tactics</li>
                  <li>✓ No need to worry about embodiment rules</li>
                  <li>⚠️ Must protect King at all costs - NO second chance</li>
                  <li>⚠️ Opponent has resurrection ability</li>
                </ul>

                <h4>Handicap Balance:</h4>
                <p>This mode creates an interesting balance:</p>
                <ul>
                  <li><strong>Royal Chess Player:</strong> Has "extra life" but Duke is tactically different from Bishop</li>
                  <li><strong>Standard Chess Player:</strong> Traditional gameplay but must play more defensively</li>
                  <li>Ideal for players of different skill levels or experimenting with asymmetric gameplay</li>
                </ul>
              </div>

              <h3>🏁 Game End Conditions:</h3>
              <ul>
                <li><strong>Royal Chess Player Wins:</strong> Checkmate/capture Standard player's King</li>
                <li><strong>Standard Chess Player Wins:</strong> 
                  <ul>
                    <li>Checkmate Royal player's original King (before Duke transforms)</li>
                    <li>OR checkmate/capture the transformed Duke-King (after transformation)</li>
                  </ul>
                </li>
                <li><strong>Draw:</strong> Stalemate for either player</li>
              </ul>
            </div>

            <div className="important-note">
              <strong>💡 Perfect For:</strong> Players of different skill levels, teaching new players about Duke mechanics, or adding strategic variety to games!
            </div>
          </section>
          )}
        </div>

        <div className="rules-actions">
          <button className="btn btn-secondary" onClick={onBack}>
            {isAuthenticated ? '← Back to Lobby' : 'Back to Cover'}
          </button>
          {hasActiveGame && (
            <button className="btn btn-success" onClick={onBackToGame}>
              ← Back to Game
            </button>
          )}
          <button className="btn btn-primary" onClick={onContinue} data-testid="rules-continue-btn">
            Continue to Game Setup
          </button>
        </div>
      </div>
    </div>
  );
};

export default RulesScreen;